import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { IdleStateEnum } from './modules/core/dtos/idle-state.enum';
import { IdleDetectingService } from './modules/core/services/idle-detecting.service';
import { ProgressData } from './modules/shared/dtos/progress-data';
import { ProgressIndicatingService } from './modules/shared/services/progress-indicating.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    private _idleState$: Observable<IdleStateEnum> = new Subject<IdleStateEnum>();
    private _unsubscribe$: Subject<void> = new Subject<void>();
    public title = 'ExternalTransactionsAdministrationPortal';
    public items: MenuItem[] = [];
    public sideMenuItems: MenuItem[] = [];
    public activePath = '';
    public currentRoute$: Observable<NavigationEnd> = new Subject<NavigationEnd>();
    public isLoggedIn: boolean = false;
    public inactiveStatus: boolean = false;
    public isAdmin: boolean = false;
    public isCommon: boolean = false;
    public minimizedNav: boolean = false;
    private progress$ = new Subject<ProgressData>();

    public readonly ADMIN_FUNCTIONS = 'admin_functions';
    public readonly COMMON_REQUESTS = 'common_requests';
    public activeMenuGroup: string = this.ADMIN_FUNCTIONS;

    public constructor(
        private idleDetectingService: IdleDetectingService,
        private router: Router,
        private confirmationService: ConfirmationService,
        private progressService: ProgressIndicatingService
    ) {
    }

    public ngOnInit(): void {
        this.currentRoute$ = this.router.events.pipe(
            filter((event) =>
                event instanceof NavigationEnd
            )
        ) as Observable<NavigationEnd>;
        this.currentRoute$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((event: NavigationEnd) => {
                this.activePath = event.url;
            });

        this._idleState$ = this.idleDetectingService.getIdleState();
        this._idleState$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((state: IdleStateEnum): void =>
                this.handleIdleState(state)
            );
    }

    public ngOnDestroy(): void {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    public onLoginStatusChanged($event: boolean): void {
        this.isLoggedIn = $event;
        if (this.isLoggedIn) {
            this.progressService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Acquiring Access Rights',
                    details: 'Please wait...',
                    done: false
                }
            });
            this.idleDetectingService.reset();
        }
    }

    public onAdminAccessChanged($event: boolean): void {
        this.isAdmin = $event;
        this.sendProgressDoneSignal();
    }

    public onCommonAccessChanged($event: boolean): void {
        this.isCommon = $event;
        this.sendProgressDoneSignal();
    }

    public onMinimizedNavClicked(): void {
        this.minimizedNav = !this.minimizedNav;
    }

    private handleIdleState(state: IdleStateEnum): void {
        if (state === IdleStateEnum.TIMED_OUT) {
            this.inactiveStatus = true;
            this.confirmationService.confirm({
                header: 'You have been logged out',
                acceptLabel: 'Ok',
                rejectVisible: false,
                message:
                    'You have been inactive for a while. You\'re logged out for security reason. Please login again to continue using the app.'
            });
        }
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }
}
